import React, { useState, useEffect, useMemo, useRef } from 'react';
import Swal from 'sweetalert2';
import { 
  FaSearch, FaPlus, FaEdit, FaTrash, FaTimes, FaChevronDown, FaChevronUp, 
  FaUserTie, FaFileExport, FaUnlock 
} from 'react-icons/fa';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../api/axiosInstance';
import Pagination from '../Pagination/Pagination';
import './AddDonor.css';
import statesDistrictsData from '../../JsonData/india-states-districts-latest.json';

const SmartFilterDropdown = ({ value, placeholder, options, onSelect, onClear, searchPlaceholder, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => { setHighlighted(0); }, [search, open]);
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector('.admin-donor-drop-highlighted');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  const handleKey = e => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0 && highlighted < filtered.length) { onSelect(filtered[highlighted]); setOpen(false); setSearch(''); }
    } else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className={`admin-donor-filter-wrap ${disabled ? 'disabled-wrap' : ''}`} ref={ref}>
      <div className={`admin-donor-filter-trigger ${open ? 'active' : ''}`} onClick={() => { if(!disabled) { setOpen(o => !o); setSearch(''); } }}>
        <span className={`admin-donor-filter-text ${value ? 'selected' : ''}`}>
          {value || placeholder}
        </span>
        <div className="admin-donor-trigger-icons">
          {value && !disabled && <FaTimes className="admin-donor-clear-icon" onClick={(e) => { e.stopPropagation(); onClear(); }} title="Clear Filter" />}
          <FaChevronDown size={10} className="admin-donor-chevron" />
        </div>
      </div>
      {open && (
        <div className="admin-donor-filter-menu">
          <input type="text" placeholder={searchPlaceholder || "Search..."} value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleKey} autoFocus />
          <ul ref={listRef}>
            {filtered.length > 0 ? filtered.map((o, i) => (
              <li key={i} className={i === highlighted ? 'admin-donor-drop-highlighted' : ''} onMouseEnter={() => setHighlighted(i)} onClick={() => { onSelect(o); setOpen(false); setSearch(''); }}>
                {o}
              </li>
            )) : <div className="admin-donor-drop-empty">No results found</div>}
          </ul>
        </div>
      )}
    </div>
  );
};

const initialFormState = { 
  memberId: '', 
  name: '', 
  fatherName: '', 
  occupation: '', 
  mobileNo: '', 
  email: '', 
  gender: '', 
  birthDate: '', 
  aadharNo: '', 
  panNo: '', 
  permanentAddress: '', 
  currentAddress: '', 
  isSameAddress: false, 
  state: '', 
  district: '' 
};

const AddDonor = () => {
  const [donors, setDonors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); 
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [editId, setEditId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isMemberIdUnlocked, setIsMemberIdUnlocked] = useState(false); 

  useEffect(() => { fetchDonors(); }, []);

  const fetchDonors = async () => {
    try {
      const res = await axiosInstance.get('/api/donors');
      setDonors(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (error) { console.error(error); }
  };

  const allStates = useMemo(() => statesDistrictsData.map(item => item.state).filter(Boolean).sort(), []);
  const availableDistricts = useMemo(() => {
    if (stateFilter) {
      const found = statesDistrictsData.find(item => item.state === stateFilter);
      return found ? found.districts.sort() : [];
    }
    const all = statesDistrictsData.flatMap(item => item.districts || []);
    return [...new Set(all)].sort();
  }, [stateFilter]);

  const existingMember = useMemo(() => {
    if(form.memberId && form.memberId.trim() !== '') {
      return donors.find(d => 
        d.memberId?.toLowerCase() === form.memberId.toLowerCase() && 
        d._id !== editId &&
        !d.isDeleted
      );
    }
    return null;
  }, [form.memberId, donors, editId]);

  const existingMobile = useMemo(() => {
    if (form.mobileNo && form.mobileNo.trim() !== '') {
      return donors.find(d => 
        d.mobileNo === form.mobileNo && 
        d._id !== editId &&
        !d.isDeleted
      );
    }
    return null;
  }, [form.mobileNo, donors, editId]);

  const activeDonors = useMemo(() => {
    let list = donors.filter(d => !d.isDeleted);
    if (stateFilter) list = list.filter(d => d.state === stateFilter);
    if (districtFilter) list = list.filter(d => d.district === districtFilter);
    if (typeFilter) {
      list = list.filter(d => 
        (d.donations || []).some(don => don.donationType === typeFilter)
      );
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(d => 
        (d.name && d.name.toLowerCase().includes(q)) || 
        (d.memberId && d.memberId.toLowerCase().includes(q)) || 
        (d.mobileNo && d.mobileNo.includes(q)) ||
        (d.fatherName && d.fatherName.toLowerCase().includes(q))
      );
    }
    return list;
  }, [donors, searchQuery, stateFilter, districtFilter, typeFilter]);

  const paginatedDonors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return activeDonors.slice(startIndex, startIndex + itemsPerPage);
  }, [activeDonors, currentPage, itemsPerPage]);

  const handleSaveDonor = async () => {
    if (!form.name) return Swal.fire('Warning', 'Name is required', 'warning');
    if (!form.fatherName) return Swal.fire('Warning', 'Father Name is required', 'warning');
    if (!form.mobileNo) return Swal.fire('Warning', 'Mobile No is required', 'warning');
    if (form.mobileNo.length !== 10) return Swal.fire('Warning', 'Mobile No must be exactly 10 digits', 'warning');
    if (!form.state) return Swal.fire('Warning', 'State is required', 'warning');
    if (!form.district) return Swal.fire('Warning', 'District is required', 'warning');
    if (!form.currentAddress) return Swal.fire('Warning', 'Current Address is required', 'warning');
    if (!form.permanentAddress) return Swal.fire('Warning', 'Permanent Address is required', 'warning');
    if (existingMember) return Swal.fire('Warning', 'This Member ID is already assigned to another donor.', 'warning');
    if (existingMobile) return Swal.fire('Warning', 'A donor with this mobile number already exists.', 'warning');

    try {
      const payload = {};
      Object.keys(form).forEach(key => {
        if (key === 'isSameAddress') {
          payload[key] = !!form[key];
        } else if (form[key] !== undefined && form[key] !== null && form[key].toString().trim() !== '') {
          payload[key] = form[key].toString().trim();
        }
      });

      if (editId) {
        await axiosInstance.put(`/api/donors/${editId}`, payload);
        Swal.fire('Updated', 'Donor profile updated successfully', 'success');
      } else {
        await axiosInstance.post('/api/donors', payload);
        Swal.fire('Added', 'New donor added successfully', 'success');
      }
      setShowAddForm(false); setForm(initialFormState); setEditId(null); setIsMemberIdUnlocked(false); fetchDonors();
    } catch (error) { 
      Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error'); 
    }
  };

  const handleEdit = (d) => { 
    setForm({
      memberId: d.memberId || '', 
      name: d.name || '', 
      fatherName: d.fatherName || '',
      occupation: d.occupation || '',
      mobileNo: d.mobileNo || '',
      email: d.email || '',
      gender: d.gender || '', 
      birthDate: d.birthDate ? d.birthDate.split('T')[0] : '',
      aadharNo: d.aadharNo || '', 
      panNo: d.panNo || '', 
      permanentAddress: d.permanentAddress || '',
      currentAddress: d.currentAddress || '',
      isSameAddress: !!d.isSameAddress,
      state: d.state || '', 
      district: d.district || ''
    });
    setEditId(d._id); 
    setIsMemberIdUnlocked(false); 
    setShowAddForm(true); 
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  };
  
  const handleDelete = async (donor) => {
    if (donor.donations && donor.donations.length > 0) {
      Swal.fire({
        title: 'Cannot Delete',
        text: `This donor has ${donor.donations.length} donation(s). You can only disable (Deactivate) this profile using the Status toggle.`,
        icon: 'error',
        confirmButtonColor: '#3498db'
      });
      return;
    }

    const res = await Swal.fire({ title: 'Delete Donor?', text: 'This will mark the donor as deleted. Member ID will be freed.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c' });
    if (res.isConfirmed) { await axiosInstance.delete(`/api/donors/${donor._id}`); fetchDonors(); }
  };
   
  const hasExistingMemberId = useMemo(() => {
    if (!editId) return false;
    const originalDonor = donors.find(d => d._id === editId);
    return originalDonor && originalDonor.memberId && originalDonor.memberId.trim() !== '';
  }, [editId, donors]);

  const isMemberIdDisabled = useMemo(() => {
    return hasExistingMemberId && !isMemberIdUnlocked;
  }, [hasExistingMemberId, isMemberIdUnlocked]);

  const toggleStatus = async (id, status) => { 
    await axiosInstance.put(`/api/donors/${id}/status`, { isActive: !status }); fetchDonors(); 
  };

  const handleCurrentAddressChange = (val) => {
    setForm(prev => ({
      ...prev,
      currentAddress: val,
      permanentAddress: prev.isSameAddress ? val : prev.permanentAddress
    }));
  };

  const handleSameAddressCheckboxChange = (checked) => {
    setForm(prev => ({
      ...prev,
      isSameAddress: checked,
      permanentAddress: checked ? prev.currentAddress : ''
    }));
  };

  const getBadgeClass = (type) => {
    if (type.includes('प्रोत्साहन') || type.includes('Encouragement')) {
      return 'admin-donor-badge-nidhi';
    }
    if (type.includes('सामान्य') || type.includes('General')) {
      return 'admin-donor-badge-general';
    }
    return 'admin-donor-badge-shiksha';
  };

  const handleExportExcel = async () => {
    if (activeDonors.length === 0) return Swal.fire('Notice', 'No data to export.', 'info');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Donors Database');

    // Reference definitions for contribution evaluation
    const allContributionTypes = [
      { name: 'General Donation (सामान्य दान)', header: 'General Donation (सामान्य दान)' },
      { name: 'Shiksha Nek (शिक्षा दान)', header: 'Shiksha Nek (शिक्षा दान)' },
      { name: 'Encouragement Fund (प्रोत्साहन निधि)', header: 'Encouragement Fund (प्रोत्साहन निधि)' }
    ];

    // Filter export columns dynamically depending on the UI filter selection
    const activeExportTypes = typeFilter 
      ? allContributionTypes.filter(t => t.name === typeFilter)
      : allContributionTypes;

    // Helper: Dynamic base-26 column letters conversion
    const getColumnLetter = (colIndex) => {
      let letter = '';
      while (colIndex > 0) {
        let temp = (colIndex - 1) % 26;
        letter = String.fromCharCode(65 + temp) + letter;
        colIndex = Math.floor((colIndex - temp) / 26);
      }
      return letter;
    };

    const maxColLetter = getColumnLetter(15 + activeExportTypes.length + 1);

    // Dynamic Title Row Merge
    worksheet.mergeCells(`A1:${maxColLetter}1`);
    worksheet.getCell('A1').value = 'RAJPUT SHIKSHA KOSH';
    worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFB91C1C' } };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

    // Dynamic Subtitle Row Merge
    worksheet.mergeCells(`A2:${maxColLetter}2`);
    worksheet.getCell('A2').value = 'Donor Directory & Profiles (दाता निर्देशिका सूची)';
    worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
    worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

    // Dynamic Info Header Merge
    worksheet.mergeCells(`A3:${maxColLetter}3`);
    worksheet.getCell('A3').value = `Search: ${searchQuery || 'None'}  |  State Filter: ${stateFilter || 'All'}  |  District Filter: ${districtFilter || 'All'}`;
    worksheet.getCell('A3').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF64748B' } };
    worksheet.getCell('A3').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.addRow([]); // Empty spacing row

    const baseHeadersBefore = [
      'S.No', 'Member ID', 'Full Name', "Father's Name", 'Occupation', 'Mobile No', 'Email ID', 
      'Gender', 'Birth Date', 'Aadhar No', 'PAN Card', 'State', 'District', 'Current Address', 
      'Permanent Address'
    ];

    const baseHeadersAfter = ['Status'];

    const headers = [
      ...baseHeadersBefore,
      ...activeExportTypes.map(t => t.header),
      ...baseHeadersAfter
    ];

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, name: 'Arial', size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    activeDonors.forEach((donor, i) => {
      const donations = donor.donations || [];
      
      // Map checkmarks dynamically depending on contribution status
      const contributionValues = activeExportTypes.map(t => {
        const hasContributed = donations.some(
          don => don.donationType === t.name && (don.txnStatus === "Success" || !don.txnStatus)
        );
        return hasContributed ? '✔' : 'N/A';
      });

      const formattedBirthDate = donor.birthDate ? new Date(donor.birthDate).toLocaleDateString('en-IN') : '—';
      
      const rowValues = [
        i + 1,
        donor.memberId || 'N/A', 
        donor.name,
        donor.fatherName || '—',
        donor.occupation || '—',
        donor.mobileNo,
        donor.email || '—',
        donor.gender || '—',
        formattedBirthDate,
        donor.aadharNo || '—',
        donor.panNo || '—',
        donor.state || '—',
        donor.district || '—',
        donor.currentAddress || '—',
        donor.permanentAddress || '—',
        ...contributionValues,
        donor.isActive ? 'Active' : 'Disabled'
      ];

      const row = worksheet.addRow(rowValues);

      row.eachCell((cell, colIndex) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.font = { name: 'Arial', size: 9 };
        
        // Text alignment layout map
        if ([3, 4, 5, 7, 14, 15].includes(colIndex)) {
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        } else {
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        }
      });

      // Apply dynamic colors to status block at the end column
      const statusCell = row.getCell(rowValues.length);
      statusCell.font = { bold: true, name: 'Arial', size: 9, color: { argb: donor.isActive ? 'FF16A34A' : 'FF94A3B8' } };
    });

    const baseColWidthsBefore = [
      { width: 8 },   
      { width: 14 },  
      { width: 22 },  
      { width: 22 },  
      { width: 18 },  
      { width: 15 },  
      { width: 25 },  
      { width: 12 },  
      { width: 14 },  
      { width: 16 },  
      { width: 14 },  
      { width: 18 },  
      { width: 18 },  
      { width: 35 },  
      { width: 35 }   
    ];

    const contribWidths = activeExportTypes.map(() => ({ width: 20 }));
    const statusWidth = [{ width: 12 }];

    worksheet.columns = [
      ...baseColWidthsBefore,
      ...contribWidths,
      ...statusWidth
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Donors_Database.xlsx');
  };

  return (
    <>
    <Helmet>
      <title>Donors | Rajput Shiksha Kosh</title>
      <meta name="description" content="Manage Donors for the Rajput Shiksha Kosh organization." />
    </Helmet>
    <div className="admin-donor-container">
      <h2 className="admin-donor-title"><FaUserTie style={{marginRight: '8px'}}/> Donor Management</h2>
      <div className="admin-donor-controls">
        <div className="admin-donor-search-wrapper">
          <FaSearch className="admin-donor-search-icon" />
          <input type="text" className="admin-donor-search-input" placeholder="Search ID, Name, Father's Name, or Mobile..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <div className="admin-donor-filter-group">
          <SmartFilterDropdown value={stateFilter} placeholder="All States" options={allStates} onSelect={(val) => { setStateFilter(val); setDistrictFilter(''); }} onClear={() => { setStateFilter(''); setDistrictFilter(''); }} searchPlaceholder="Search State..."/>
          <SmartFilterDropdown value={districtFilter} placeholder="All Districts" options={availableDistricts} onSelect={(val) => setDistrictFilter(val)} onClear={() => setDistrictFilter('')} searchPlaceholder="Search District..."/>
          
          {/* Donation Type Filter Dropdown */}
          <select 
            value={typeFilter} 
            onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }} 
            className="admin-donor-items-select"
          >
            <option value="">All Donation Types</option>
            <option value="General Donation (सामान्य दान)">General Donation (सामान्य दान)</option>
            <option value="Shiksha Nek (शिक्षा दान)">Shiksha Nek (शिक्षा दान)</option>
            <option value="Encouragement Fund (प्रोत्साहन निधि)">Encouragement Fund (प्रोत्साहन निधि)</option>
          </select>

          <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="admin-donor-items-select">
            <option value="5">5 / page</option>
            <option value="10">10 / page</option>
            <option value="20">20 / page</option>
            <option value="30">30 / page</option>
            <option value="50">50 / page</option>
          </select>
        </div>
        <div className="admin-donor-action-buttons-wrap">
          <button className="admin-donor-btn admin-donor-btn-export" onClick={handleExportExcel} title="Export Filtered Excel">
            <FaFileExport /> Export Excel
          </button>
          <button className="admin-donor-btn admin-donor-btn-add" onClick={() => { setShowAddForm(!showAddForm); setEditId(null); setForm(initialFormState); setIsMemberIdUnlocked(false); }}>
            {showAddForm ? <><FaTimes /> Close Form</> : <><FaPlus /> Add Donor</>}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="admin-donor-inline-form">
          <h3 className="admin-donor-form-title">{editId ? '✏️ Edit Donor Profile' : 'Add New Donor Profile'}</h3>
          
          <h4 className="admin-donor-section-title">Personal & Contact Details</h4>
          <div className="admin-donor-form-grid">
            
            <div className="admin-donor-form-group">
              <label>Member ID</label>
              <div className="admin-donor-memid-input-wrapper">
                <input 
                   type="text" 
                   value={form.memberId} 
                   onChange={e => setForm({...form, memberId: e.target.value})} 
                   placeholder="e.g. M-101" 
                   disabled={isMemberIdDisabled}
                />
                {hasExistingMemberId && !isMemberIdUnlocked && (
                  <button 
                    type="button" 
                    className="admin-donor-btn-edit-unlock" 
                    onClick={() => setIsMemberIdUnlocked(true)}
                    title="Unlock to Edit Member ID"
                  >
                    <FaUnlock />
                  </button>
                )}
              </div>
              {isMemberIdDisabled && <span className="admin-donor-form-hint">* Click the edit button to unlock and modify this assigned Member ID.</span>}
              {existingMember && !isMemberIdDisabled && (
                 <span className="admin-donor-validation-warning">
                   ⚠️ ID Exists: {existingMember.name} ({existingMember.mobileNo})
                 </span>
              )}
            </div>
            <div className="admin-donor-form-group">
              <label>Full Name <span className="admin-donor-req">*</span></label>
              <input type="text" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} placeholder="Donor Name" />
            </div>
            <div className="admin-donor-form-group">
              <label>Father's Name <span className="admin-donor-req">*</span></label>
              <input type="text" value={form.fatherName} onChange={e=>setForm({...form, fatherName: e.target.value})} placeholder="Father's Name" />
            </div>
            <div className="admin-donor-form-group">
              <label>Occupation</label>
              <input type="text" value={form.occupation} onChange={e=>setForm({...form, occupation: e.target.value})} placeholder="Occupation" />
            </div>
            <div className="admin-donor-form-group">
              <label>Mobile No <span className="admin-donor-req">*</span></label>
              <input type="text" value={form.mobileNo} onChange={e=>setForm({...form, mobileNo: e.target.value.replace(/\D/g, '')})} placeholder="10-digit Mobile" maxLength="10"/>
              {existingMobile && (
                 <span className="admin-donor-validation-warning">
                   ⚠️ Mobile Exists: {existingMobile.name}
                 </span>
              )}
            </div>
            <div className="admin-donor-form-group">
              <label>Email ID</label>
              <input type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} placeholder="Email Address" />
            </div>
            <div className="admin-donor-form-group">
              <label>Gender</label>
              <select value={form.gender} onChange={e=>setForm({...form, gender: e.target.value})}>
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="admin-donor-form-group">
              <label>Birth Date</label>
              <input type="date" value={form.birthDate} onChange={e=>setForm({...form, birthDate: e.target.value})} />
            </div>
            <div className="admin-donor-form-group">
              <label>Aadhar Number</label>
              <input type="text" value={form.aadharNo} onChange={e=>setForm({...form, aadharNo: e.target.value.replace(/\D/g, '')})} placeholder="12-digit Aadhar" maxLength="12"/>
            </div>
            <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group admin-donor-form-group">
              <label>PAN Card Number</label>
              <input type="text" value={form.panNo} onChange={e=>setForm({...form, panNo: e.target.value.toUpperCase()})} placeholder="PAN Number" maxLength="10"/>
            </div>
          </div>

          <h4 className="admin-donor-section-title">Address & Demographics</h4>
          <div className="admin-donor-form-grid">
            <div className="admin-donor-form-group">
              <label>State <span className="admin-donor-req">*</span></label>
              <SmartFilterDropdown value={form.state} placeholder="Select State" options={allStates} onSelect={(val) => setForm({...form, state: val, district: ''})} onClear={() => setForm({...form, state: '', district: ''})} searchPlaceholder="Search State..."/>
            </div>
            <div className="admin-donor-form-group">
              <label>District <span className="admin-donor-req">*</span></label>
              <SmartFilterDropdown 
                value={form.district} placeholder={form.state ? "Select District" : "Select State First"} 
                options={form.state ? (statesDistrictsData.find(s => s.state === form.state)?.districts || []) : []}
                onSelect={(val) => setForm({...form, district: val})} onClear={() => setForm({...form, district: ''})} 
                searchPlaceholder="Search District..."
                disabled={!form.state}
              />
            </div>
            
            <div className="admin-donor-form-group admin-donor-col-span-2">
              <label>Current Address <span className="admin-donor-req">*</span></label>
              <input 
                type="text" 
                value={form.currentAddress} 
                onChange={e=>handleCurrentAddressChange(e.target.value)} 
                placeholder="Current Address" 
              />
            </div>

            <div className="admin-donor-checkbox-row admin-donor-col-span-2">
              <label className="admin-donor-checkbox-label">
                <input 
                  type="checkbox" 
                  checked={form.isSameAddress} 
                  onChange={e => handleSameAddressCheckboxChange(e.target.checked)} 
                />
                <span>Permanent address is same as Current address</span>
              </label>
            </div>

            <div className="admin-donor-form-group admin-donor-col-span-2">
              <label>Permanent Address <span className="admin-donor-req">*</span></label>
              <input 
                type="text" 
                value={form.permanentAddress} 
                onChange={e=>setForm({...form, permanentAddress: e.target.value})} 
                placeholder={form.isSameAddress ? "Same as Current Address" : "Permanent Address"} 
                disabled={form.isSameAddress}
                style={form.isSameAddress ? { background: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' } : {}}
              />
            </div>
          </div>

          <div className="admin-donor-form-buttons">
            <button className="admin-donor-btn-cancel" onClick={() => setShowAddForm(false)}>Cancel</button>
            <button className="admin-donor-btn-confirm" onClick={handleSaveDonor}>{editId ? 'Update Donor' : 'Save Donor'}</button>
          </div>
        </div>
      )}

      {/* ── Table Section ── */}
      <div className="admin-donor-table-wrapper">
        <table className="admin-donor-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th className="admin-donor-table-memid">Member ID</th>
              <th className="admin-donor-align-left">Donor Name</th>
              <th className="admin-donor-table-father">Father's Name</th>
              <th className="admin-donor-table-mob">Mobile No</th>
              <th className="admin-donor-table-state">State</th>
              <th className="admin-donor-table-district">District</th>
              <th className="admin-donor-table-status">Status</th>
              <th className="admin-donor-table-action">Action</th>
            </tr>
          </thead>
          <tbody>
            {activeDonors.length === 0 ? (
              <tr><td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No donor records found.</td></tr>
            ) : paginatedDonors.map((d, i) => (
              <React.Fragment key={d._id}>
                <tr className="admin-donor-main-row">
                  <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                  <td className={`admin-donor-table-memid ${!d.memberId ? 'member-id-na' : ''}`}>
                    {d.memberId || 'N/A'}
                  </td>
                  <td className="admin-donor-align-left">
                    <div className="admin-donor-name-wrapper" onClick={() => setExpandedRow(expandedRow === d._id ? null : d._id)}>
                      <span style={{ fontWeight: 600 }}>{d.name}</span>
                      <button className="admin-donor-expand-btn">
                        {expandedRow === d._id ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    </div>
                  </td>
                  <td className="admin-donor-table-father">{d.fatherName}</td>
                  <td className="admin-donor-table-mob">{d.mobileNo}</td>
                  <td className="admin-donor-table-state">{d.state || '—'}</td>
                  <td className="admin-donor-table-district">{d.district || '—'}</td>
                  <td className="admin-donor-table-status">
                    <label className="admin-donor-switch"><input type="checkbox" checked={d.isActive} onChange={() => toggleStatus(d._id, d.isActive)} /><span className="admin-donor-slider" /></label>
                    <div style={{ fontSize: '0.75rem', marginTop: '4px', color: d.isActive ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>{d.isActive ? 'Active' : 'Disabled'}</div>
                  </td>
                  <td className="admin-donor-table-action">
                    <div className="admin-donor-action-btns">
                      <button className="admin-donor-btn-edit" onClick={() => handleEdit(d)} title="Edit Profile"><FaEdit /></button>
                      <button className="admin-donor-btn-delete" onClick={() => handleDelete(d)} title="Delete Profile"><FaTrash /></button>
                    </div>
                  </td>
                </tr>
                {expandedRow === d._id && (
                  <tr className="admin-donor-expanded-row">
                    <td colSpan="9">
                      <div className="admin-donor-expanded-content">
                        {/* Always visible inside expand view as they aren't part of the main default columns */}
                        <div className="admin-donor-exp-item"><span className="admin-donor-exp-label">Occupation</span><span className="admin-donor-exp-value">{d.occupation || '—'}</span></div>
                        <div className="admin-donor-exp-item"><span className="admin-donor-exp-label">Email ID</span><span className="admin-donor-exp-value">{d.email || '—'}</span></div>
                        <div className="admin-donor-exp-item"><span className="admin-donor-exp-label">Gender</span><span className="admin-donor-exp-value">{d.gender || '—'}</span></div>
                        <div className="admin-donor-exp-item"><span className="admin-donor-exp-label">Birth Date</span><span className="admin-donor-exp-value">{d.birthDate ? new Date(d.birthDate).toLocaleDateString('en-IN') : '—'}</span></div>
                        <div className="admin-donor-exp-item"><span className="admin-donor-exp-label">Aadhar Number</span><span className="admin-donor-exp-value">{d.aadharNo || '—'}</span></div>
                        <div className="admin-donor-exp-item"><span className="admin-donor-exp-label">PAN Number</span><span className="admin-donor-exp-value">{d.panNo || '—'}</span></div>
                        <div className="admin-donor-col-span-full admin-donor-exp-item"><span className="admin-donor-exp-label">Current Address</span><span className="admin-donor-exp-value">{d.currentAddress || '—'}</span></div>
                        <div className="admin-donor-col-span-full admin-donor-exp-item"><span className="admin-donor-exp-label">Permanent Address</span><span className="admin-donor-exp-value">{d.permanentAddress || '—'}</span></div>

                        {/* Subscription badges block in expand view - displays active donation types contributed by the donor with border-radius: 0px */}
                        <div className="admin-donor-col-span-full admin-donor-exp-item admin-donor-exp-subscriptions-section">
                          <span className="admin-donor-exp-label active-subscriptions-label">Contributed Donation Categories (योगदान श्रेणियां)</span>
                          <div className="admin-donor-badges-row">
                            {(() => {
                              const uniqueTypes = Array.from(new Set((d.donations || []).map(don => don.donationType)));
                              if (uniqueTypes.length === 0) {
                                return <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>No donations recorded yet (कोई योगदान नहीं)</span>;
                              }
                              return uniqueTypes.map((type, idx) => (
                                <span 
                                  key={idx} 
                                  className={`admin-donor-subscription-badge ${getBadgeClass(type)}`}
                                >
                                  {type}
                                </span>
                              ));
                            })()}
                          </div>
                        </div>

                        {/* Responsive elements toggled dynamically by screensize */}
                        <div className="admin-donor-exp-item admin-donor-exp-memid">
                          <span className="admin-donor-exp-label">Member ID</span>
                          <span className={`admin-donor-exp-value ${!d.memberId ? 'member-id-na' : ''}`}>
                            {d.memberId || 'N/A'}
                          </span>
                        </div>
                        <div className="admin-donor-exp-item admin-donor-exp-father"><span className="admin-donor-exp-label">Father's Name</span><span className="admin-donor-exp-value">{d.fatherName}</span></div>
                        <div className="admin-donor-exp-item admin-donor-exp-mob"><span className="admin-donor-exp-label">Mobile Number</span><span className="admin-donor-exp-value">{d.mobileNo}</span></div>
                        <div className="admin-donor-exp-item admin-donor-exp-state"><span className="admin-donor-exp-label">State</span><span className="admin-donor-exp-value">{d.state || '—'}</span></div>
                        <div className="admin-donor-exp-item admin-donor-exp-district"><span className="admin-donor-exp-label">District</span><span className="admin-donor-exp-value">{d.district || '—'}</span></div>
                        
                        <div className="admin-donor-exp-item admin-donor-exp-status">
                          <span className="admin-donor-exp-label">Status</span>
                          <div className="admin-donor-exp-status-wrapper">
                            <label className="admin-donor-switch"><input type="checkbox" checked={d.isActive} onChange={() => toggleStatus(d._id, d.isActive)} /><span className="admin-donor-slider" /></label>
                            <span className="admin-donor-exp-status-text">{d.isActive ? 'Active' : 'Disabled'}</span>
                          </div>
                        </div>
                        <div className="admin-donor-exp-item admin-donor-exp-action">
                          <span className="admin-donor-exp-label">Actions</span>
                          <div className="admin-donor-exp-actions-wrapper">
                            {/* Text labels removed; only icons displayed now */}
                            <button className="admin-donor-btn-edit" onClick={() => handleEdit(d)} title="Edit Profile"><FaEdit /></button>
                            <button className="admin-donor-btn-delete" onClick={() => handleDelete(d)} title="Delete Profile"><FaTrash /></button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination 
        totalItems={activeDonors.length}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </div>
    </>
  );
};

export default AddDonor;